/*
 * @Author: Teemo Ding
 * @Date: 2022-02-16 17:48:58
 * @LastEditTime: 2022-06-06 11:07:16
 * @LastEditors: Json Chen
 * @FilePath: /unifyxp-v2-store-portal/src/pages/Settings/Communication/Components/FormComponents/index.tsx
 */
import {
  Input,
  Label,
  RadioGroup,
  RadioItem,
  Select,
  TextArea,
} from '@/src/components/Antd';
import Configuration from '@/src/components/Configuration/Configuration';
import Row from '@/src/components/Layouts/Row/Row';
import Link from '@/src/components/Link/Link';
import PasswordInput from '@/src/components/Password';
import { MobileNumberAreaInfo } from '@/src/pages/CustomerDatabaseConfig/Components/CustomerInfoModal';
import { NEW } from '@/src/tools/constant';
import { Icon } from '@/src/tools/icon';
import {
  CommunicationChannelType,
  SendFromType,
} from '@/src/types/common/communication';
import { InfoCircleOutlined } from '@ant-design/icons';
import { Form } from 'antd';
import React, { Children, Fragment, cloneElement, useState } from 'react';
import { useI18n } from 'src/hooks/useRouterChange';
import { i18nGet, replaceI18nVariables } from 'src/tools/tools';
import css from './css.module.less';
import { useGetAreaCodeText } from 'src/hooks/useGetAreaCodeText';
import { get } from 'lodash';

const mobileIsValid = (value) => {
  const { areaCodeInfo, mobileNumber } = value || {};
  const num = mobileNumber ? mobileNumber.toString() : null;
  const hasValue = !num || !get(areaCodeInfo, 'uuid');
  return hasValue;
};

interface IProps {
  edit;
  value?;
  onChange?;
  options?;
  children;
  disabled?: boolean;
}

export const InputWithValue = ({
  edit,
  value,
  onChange,
  children,
  disabled,
}: IProps) => {
  const childrenWithProps = Children.map(children, (child) => {
    return cloneElement(child, { value, onChange });
  });
  return !edit ? <div>{value}</div> : childrenWithProps;
};

export const RadioWithValue = ({
  edit,
  value,
  onChange,
  options,
  children,
}: IProps) => {
  const childrenWithProps = Children.map(children, (child) => {
    return cloneElement(child, { value, onChange });
  });
  const indexOption = options.filter((o) => o.value === value);
  return !edit ? <div>{(indexOption[0] || {}).label}</div> : childrenWithProps;
};

const PhoneWithValue = ({
  edit,
  value,
  onChange,
  // options,
  children,
}: IProps) => {
  const { areaCodeInfo, mobileNumber } = value || {};
  const areaCodeInText = useGetAreaCodeText(get(areaCodeInfo, 'uuid'));

  const childrenWithProps = Children.map(children, (child) => {
    return cloneElement(child, { value, onChange });
  });
  return !edit ? (
    <div>
      {value ? '+' : ''} {areaCodeInText || ''} {mobileNumber}
    </div>
  ) : (
    childrenWithProps
  );
};

export const AccountSIDAndAuthToken = ({
  editable,
  handleResetInput,
  unlockEditable,
  communicationUuid,
  disabled,
}) => {
  const i18n = useI18n();
  const getI18n = (str) => i18nGet(i18n, str);
  return (
    <Row
      elements={[
        {
          child: (
            <Form.Item
              name='accountSid'
              rules={[
                {
                  required: true,
                  message: replaceI18nVariables(
                    getI18n('general.fieldisrequired'),
                    getI18n('storeCommunicationSetting.common.accountSID'),
                  ),
                },
              ]}
              label={
                !editable ? (
                  getI18n('storeCommunicationSetting.common.accountSID')
                ) : (
                  <Label
                    title={`${getI18n(
                      'storeCommunicationSetting.common.accountSID',
                    )}*`}
                  />
                )
              }
              labelCol={{ span: 24 }}>
              <InputWithValue edit={editable}>
                <PasswordInput
                  editable={
                    communicationUuid === NEW ||
                    unlockEditable.includes('accountSid')
                  }
                  onEdit={() => {
                    handleResetInput('accountSid');
                  }}
                  passwordProps={{ maxLength: 100 }}
                  disabled={disabled}
                />
              </InputWithValue>
            </Form.Item>
          ),
        },
        {
          child: (
            <Form.Item
              name='authToken'
              rules={[
                {
                  required: true,
                  message: replaceI18nVariables(
                    getI18n('general.fieldisrequired'),
                    getI18n('storeCommunicationSetting.common.authToken'),
                  ),
                },
              ]}
              label={
                !editable ? (
                  getI18n('storeCommunicationSetting.common.authToken')
                ) : (
                  <Label
                    title={`${getI18n(
                      'storeCommunicationSetting.common.authToken',
                    )}*`}
                  />
                )
              }
              labelCol={{ span: 24 }}>
              <InputWithValue edit={editable}>
                <PasswordInput
                  editable={
                    communicationUuid === NEW ||
                    unlockEditable.includes('authToken')
                  }
                  onEdit={() => handleResetInput('authToken')}
                  passwordProps={{ maxLength: 100 }}
                  disabled={disabled}
                />
              </InputWithValue>
            </Form.Item>
          ),
        },
      ]}
    />
  );
};

export const SendSMSFrom = ({ editable, areaCodesOptions, disabled }) => {
  const i18n = useI18n();
  const getI18n = (str) => i18nGet(i18n, str);

  const sendFromOptions = [
    {
      value: SendFromType.Mobile,
      label: getI18n('storeCommunicationSetting.common.phoneNumber'),
      span: getI18n('storeCommunicationSetting.common.phoneNumberSpan'),
    },
    {
      value: SendFromType.SenderId,
      label: getI18n('storeCommunicationSetting.common.alphanumericSenderID'),
      span: getI18n(
        'storeCommunicationSetting.common.alphanumericSenderIDSpan',
      ),
    },
  ];
  return (
    <Fragment>
      <h3>{getI18n('storeCommunicationSetting.common.sendSMSfrom')}</h3>
      <Form.Item
        name='sendFrom'
        className={css.sendFrom}
        initialValue={SendFromType.Mobile}
        labelCol={{ span: 24 }}>
        <RadioWithValue edit={editable} options={sendFromOptions}>
          <RadioGroup disabled={disabled}>
            {sendFromOptions.map((item) => (
              <div key={item.value} className={css.radioItem}>
                <RadioItem value={item.value}>{item.label}</RadioItem>
                <span className={css.exclusive}>{item.span}</span>
              </div>
            ))}
          </RadioGroup>
        </RadioWithValue>
      </Form.Item>
      <Form.Item
        noStyle
        shouldUpdate={(prevValues, curValues) =>
          prevValues.sendFrom !== curValues.sendFrom
        }>
        {({ getFieldValue }) => (
          <Row
            elements={[
              {
                child:
                  getFieldValue('sendFrom') === SendFromType.Mobile ? (
                    <Form.Item
                      className={css.phoneNumber}
                      name='phoneNumber'
                      rules={[
                        {
                          validator: async (x, value) => {
                            const isMobileNumberValid = mobileIsValid(value);
                            if (isMobileNumberValid) {
                              return Promise.reject(
                                new Error(
                                  getI18n('general.areaCodeMobileError'),
                                ),
                              );
                            }
                          },
                        },
                      ]}
                      label={
                        !editable ? (
                          getI18n(
                            'storeCommunicationSetting.common.phoneNumber',
                          )
                        ) : (
                          <Label
                            title={`${getI18n(
                              'storeCommunicationSetting.common.phoneNumber',
                            )}*`}
                          />
                        )
                      }
                      labelCol={{ span: 24 }}>
                      <PhoneWithValue edit={editable}>
                        <MobileNumberAreaInfo
                          //options={areaCodesOptions}
                          disabled={disabled}
                        />
                      </PhoneWithValue>
                    </Form.Item>
                  ) : (
                    <Fragment>
                      <Form.Item
                        name='serviceSid'
                        rules={[
                          {
                            required: true,
                            message: replaceI18nVariables(
                              getI18n('general.fieldisrequired'),
                              getI18n(
                                'storeCommunicationSetting.common.messagingServiceSID',
                              ),
                            ),
                          },
                        ]}
                        label={
                          !editable ? (
                            getI18n(
                              'storeCommunicationSetting.common.messagingServiceSID',
                            )
                          ) : (
                            <Label
                              title={`${getI18n(
                                'storeCommunicationSetting.common.messagingServiceSID',
                              )}*`}
                            />
                          )
                        }
                        labelCol={{ span: 24 }}>
                        <InputWithValue edit={editable}>
                          <Input size='large' disabled={disabled} />
                        </InputWithValue>
                      </Form.Item>
                      {editable && (
                        <Configuration
                          hideGt
                          linkTo='/'
                          configStart={getI18n(
                            'storeCommunicationSetting.common.configurationStart',
                          )}
                          configLink={getI18n(
                            'storeCommunicationSetting.common.configurationLink',
                          )}
                          configEnd=' '
                        />
                      )}
                    </Fragment>
                  ),
              },

              {
                child: null,
              },
            ]}
          />
        )}
      </Form.Item>
    </Fragment>
  );
};

export const TemplateLanguage = ({ manualOption, nameKey, disableEdit }) => {
  const i18n = useI18n();
  const getI18n = (str) => i18nGet(i18n, str);
  return (
    <Form.Item
      label={
        <Label
          title={`${getI18n(
            'storeCommunicationSetting.common.templateLanguage',
          )}*`}
        />
      }
      labelCol={{
        span: 24,
      }}
      rules={[
        {
          required: true,
          message: replaceI18nVariables(
            getI18n('general.fieldisrequired'),
            getI18n('storeCommunicationSetting.common.templateLanguage'),
          ),
        },
      ]}
      name={nameKey}>
      <Select
        disabled={disableEdit}
        className={css.manualTagsSelect}
        getPopupContainer={(trigger) => trigger}
        mode='multiple'
        size='large'
        filterOption={(input, option) =>
          option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
        }
        placeholder={getI18n(
          'storeCommunicationSetting.common.searchandselectlanguage',
        )}
        options={manualOption}
        showArrow={true}
        suffixIcon={<Icon type='iconlanguageCopy' />}></Select>
    </Form.Item>
  );
};

export const TemplateContentInput = ({
  labelName,
  nameKey,
  placeholder,
  listName,
  rules,
  channelType,
  disableEdit,
}) => {
  const i18n = useI18n();
  const getI18n = (str) => i18nGet(i18n, str);
  return (
    <Fragment>
      <Form.Item
        label={<Label title={labelName} textLength={1000} nameKey={listName} />}
        labelCol={{
          span: 24,
        }}
        rules={rules}
        name={nameKey}>
        <TextArea
          size='large'
          autoSize={{ minRows: 3, maxRows: 3 }}
          maxLength={1000}
          placeholder={placeholder}
          disabled={disableEdit}
        />
      </Form.Item>
      {channelType == CommunicationChannelType.WhatsApp && (
        <div className={css.container}>
          <div className={css.icon}>
            <InfoCircleOutlined />
          </div>
          <div>
            {getI18n(
              'storeCommunicationSetting.common.templateContentInputTitle',
            )}
            <React.Fragment>
              <Link to={'/'} className={css.tagsRightPart}>
                {getI18n(
                  'storeCommunicationSetting.common.templateContentInputLink',
                )}
              </Link>{' '}
            </React.Fragment>
          </div>
        </div>
      )}
    </Fragment>
  );
};

export const SendgridTemplateContentInput = ({
  labelName,
  nameKey,
  rules,
  form,
  validateName,
  collapsible,
  disableEdit,
}) => {
  const [collapse, setCollapse] = useState(false);

  const handleCollapse = () => {
    form.validateFields([validateName]).then((res) => {
      setCollapse(!collapse);
    });
  };

  return (
    <Form.Item
      label={
        <Label
          title={
            collapsible ? (
              <div>
                <Icon
                  className={css.collapseIcon}
                  onClick={handleCollapse}
                  type={collapse ? 'iconright' : 'icondown1'}
                />
                {labelName}
              </div>
            ) : (
              labelName
            )
          }
        />
      }
      labelCol={{
        span: 24,
      }}
      className={collapse ? css.hideTextArea : css.showTextArea}
      rules={rules}
      name={nameKey}>
      <TextArea
        size='large'
        autoSize={{ minRows: 3, maxRows: 10 }}
        disabled={disableEdit}
      />
    </Form.Item>
  );
};
